import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Navigate, useParams } from 'react-router-dom';
import { userActivation, reSendCodeSecurity, authenticate, isAuthenticated, getAtivatorData } from "../includes/auth";
import { FaShieldAlt, FaCheckCircle } from "react-icons/fa";
import { MdArrowForward, MdRefresh } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import logoelakayDiamant from '../includes/Layout/icon/logoelakayDiamant-256.png';
import CountdownTimer from './CountdownTimer';
import VerificationCodeInput from '../components/security/VerificationCodeInput';
import './user.css';
import AuthPageNav from '../includes/auth/AuthPageNav';
import { elakayAutoText } from "../includes/pageInside/dataElakay";
import { displayAuthError } from "../includes/auth/authErrors";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["active"]?.[key] || fallback;

const Active = (props) => {
    const { 
        infoSource,
        
        // === HEADER ANIMATIONS ===
        enableHeaderAnimations = false,
        enableHeaderShapes = false,
        enableLogoAnimation = false,
        enableLogoPulse = false,
        enableLogoHover = false,
        enableDecorationAnimations = false,
        
        // === PARTICLES ===
        enableParticles = false,
        particlesCount = 0,
        
        // === CARD ===
        enableCardEntrance = false,
        enableCardGlow = false,
        
        // === FORM HEADER / ICON ===
        enableTitleGlow = false,
        enableActivationIcon = false,

        // === COUNTDOWN TIMER ===
        enableCountdownTimer = true,
        enableCountdownAnimation = false,
        enableCountdownProgressBar = false,
        enableCountdownTimeText = true,
        countdownWarningThreshold = 300,  // 5 minutes
        countdownDangerThreshold = 60,    // 1 minute
        
        // === FORM INPUTS ===
        enableFormGroupFade = false,

        // === BUTTONS ===
        enableButtonHover = false,
        enableButtonIcon = false,
        
        // === ALERTS ===
        enableAlertSlide = false,
        enableLoadingDots = false,
        enableSuccessAlert = true,
        enableSuccessIcon = true,
        
        // === HELP SECTION ===
        enableHelpSection = true,
        enableResendButton = true,
        enableResendIcon = false,
        
        // === FOOTER ===
        enableFooterWaves = false,
        enableFooterLinkHover = false
    } = props;
  __setElakayInfoSource(infoSource);

    let params = useParams();

    const [values, setValues] = useState({
        token: params.token,
        numberDigi: "",
        error: "",
        errorCode: null,
        loading: false,
        Suc: false,
        redirectToReferrer: false
    });

    // === STATE POUR ATIVATOR DATA ===
    const [ativatorData, setAtivatorData] = useState(null);
    const [isCodeExpired, setIsCodeExpired] = useState(false);
    const [isLoadingAtivator, setIsLoadingAtivator] = useState(true);

    const { token, numberDigi, loading, Suc, error, redirectToReferrer } = values;

    const numberDigiRef = useRef(null);
    // Guards against double-submission when onComplete auto-fires as soon
    // as the 6th digit is entered (see clickSubmit/handleAutomaticVerification
    // below) -- reset to "" whenever the code changes after an error, so a
    // corrected code can be auto-verified again.
    const lastSubmittedCodeRef = useRef("");
    const scrollToError = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
            ref.current.focus?.();
        }
    };

    // === CHARGER LES DONNÉES ATIVATOR AU MONTAGE ===
    useEffect(() => {
        if (token) {
            loadAtivatorData();
        }
    }, [token]);

    // Fonction pour charger les données ativator depuis le serveur
    const loadAtivatorData = async () => {
        setIsLoadingAtivator(true);
        try {
            // Appel API pour obtenir les données ativator
            // Vous devez créer cette fonction dans auth.js
            const data = await getAtivatorData(token);
            
            if (data && data.ativator && data.ativator.length > 0) {
                // Prendre le dernier ativator (le plus récent)
                const latestAtivator = data.ativator[data.ativator.length - 1];
                setAtivatorData(latestAtivator);
                
                // Vérifier si déjà expiré
                if (latestAtivator.randomExpires) {
                    const expiryTime = new Date(latestAtivator.randomExpires).getTime();
                    if (Date.now() > expiryTime) {
                        setIsCodeExpired(true);
                    }
                }
            }
        } catch (error) {
            console.error("Error loading ativator data:", error);
        } finally {
            setIsLoadingAtivator(false);
        }
    };

    // === HANDLER QUAND LE CODE EXPIRE ===
    const handleCodeExpired = () => {
        setIsCodeExpired(true);
        setValues({ 
            ...values, 
            error: "", errorCode: "VERIFICATION_CODE_EXPIRED"
        });
    };

    const clickSubmit = (codeOverride) => {
        const codeToSubmit = codeOverride ?? numberDigi;

        // Vérifier si le code est expiré
        if (isCodeExpired) {
            setValues({
                ...values,
                error: "", errorCode: "VERIFICATION_CODE_EXPIRED"
            });
            return;
        }

        if (!codeToSubmit || codeToSubmit.trim().length !== 6) {
            setValues({ ...values, error: "", errorCode: "INVALID_VERIFICATION_CODE_FORMAT" });
            scrollToError(numberDigiRef);
            return;
        }

        setValues({ ...values, error: false, errorCode: null, loading: true });
        userActivation({ token, numberDigi: codeToSubmit })
            .then(data => {
                if (data.error) {
                    lastSubmittedCodeRef.current = "";
                    setValues({ ...values, error: data.error, errorCode: data.code || null, loading: false, Suc: false });
                } else {
                    // authenticate() itself opens the signed-in app (once).
                    authenticate(data, () => {
                        setValues({
                            ...values,
                            redirectToReferrer: true
                        });
                    });
                }
            });
    };

    // Auto-submit as soon as the 6th digit is entered -- lastSubmittedCodeRef
    // guards against firing twice for the same code (e.g. a stray extra
    // onComplete call) and is reset on error so a corrected code can be
    // auto-verified again.
    const handleAutomaticVerification = useCallback((code) => {
        if (!/^\d{6}$/.test(code)) return;
        if (loading || lastSubmittedCodeRef.current === code) return;
        lastSubmittedCodeRef.current = code;
        clickSubmit(code);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, isCodeExpired, values]);

    const resendCode = () => {
        setValues({ ...values, error: false, errorCode: null, loading: true });
        reSendCodeSecurity({ token, numberDigi })
            .then(data => {
                if (data.error) {
                    setValues({ ...values, error: data.error, errorCode: data.code || null, loading: false, Suc: false });
                } else {
                    // Reset l'état expiré et recharger les données ativator
                    setIsCodeExpired(false);
                    
                    // Mettre à jour les données ativator avec la nouvelle réponse
                    if (data.ativator && data.ativator.length > 0) {
                        const latestAtivator = data.ativator[data.ativator.length - 1];
                        setAtivatorData(latestAtivator);
                    }
                    
                    lastSubmittedCodeRef.current = "";
                    setValues({
                        ...values,
                        numberDigi: "",
                        Suc: true,
                        error: false, errorCode: null,
                        loading: false
                    });
                    window.location.reload();
                }
            });
    };

    const clickelakay = () => { 
        return window.location.href = "/";
    };

    // Fonction utilitaire pour extraire la date de création depuis ObjectId
    const getCreatedAtFromObjectId = (objectId) => {
        if (!objectId) return null;
        try {
            const idString = objectId.toString ? objectId.toString() : objectId;
            const timestamp = parseInt(idString.substring(0, 8), 16) * 1000;
            return new Date(timestamp).toISOString();
        } catch (e) {
            return null;
        }
    };

    // Re-computed on every render: a language switch re-translates an error
    // already displayed (errorCode + active language), no new request.
    const shownError = displayAuthError(values.errorCode, error);

    const showError = () => (
        <div
            className={`sign-alert sign-alert-danger ${!enableAlertSlide ? 'no-slide' : ''}`}
            style={{ display: shownError ? "" : "none" }}
            role="alert"
        >
            {shownError}
            {!isCodeExpired && (
                <div className="sign-alert-action">
                    <Link to="/Signup" className="sign-alert-link">
                        {infoSource.laguangeElakay('Button', infoSource.languageAPI).creatAccount}
                    </Link>
                </div>
            )}
        </div>
    );

    const showLoading = () =>
        loading && (
            <div className={`sign-alert sign-alert-loading ${!enableAlertSlide ? 'no-slide' : ''}`}>
                {enableLoadingDots && (
                    <div className="sign-loading-dots">
                        <div className="sign-dot"></div>
                        <div className="sign-dot"></div>
                        <div className="sign-dot"></div>
                    </div>
                )}
                <span className="sign-loading-text">
                    {infoSource.language(infoSource, "title", "loanding") || elakayAutoText("h_b04ba49f848624", "Loading...")}
                </span>
            </div>
        );

    const suscceSendCode = () =>
        Suc && (
            <div 
                className={`sign-alert sign-alert-success ${!enableSuccessAlert ? 'no-animation' : ''}`}
            >
                {enableSuccessIcon && <FaCheckCircle className="sign-alert-icon" />}
                <div className="sign-alert-content">
                    <p className="sign-alert-text">
                        {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text5 || 
                         "A new code has been sent to your email!"}
                    </p>
                    <p className="sign-alert-subtitle">
                        {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).checkEmail || 
                         "Please check your inbox."}
                    </p>
                </div>
            </div>
        );

    const signUpForm = () => (
        <div className={`sign-page-wrapper ${!enableHeaderAnimations ? 'no-animations' : ''}`}>
            {/* Animated Header */}
            <header className={`sign-header ${!enableHeaderAnimations ? 'no-header-animation' : ''}`}>
                {enableHeaderShapes && (
                    <div className="sign-header-bg-shapes">
                        <div className="sign-shape sign-shape-1"></div>
                        <div className="sign-shape sign-shape-2"></div>
                        <div className="sign-shape sign-shape-3"></div>
                        <div className="sign-shape sign-shape-4"></div>
                    </div>
                )}
                
                <div className="sign-header-content">
                    <div 
                        className={`sign-logo ${!enableLogoHover ? 'no-hover' : ''}`}
                        onClick={() => clickelakay()}
                    >
                        <div className="sign-logo-icon-wrapper">
                            <img 
                                className={`sign-logo-image ${!enableLogoAnimation ? 'no-bounce' : ''}`}
                                src={logoelakayDiamant} 
                                width="40" 
                                alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).active.t001}
                            />
                            {enableLogoPulse && <div className="sign-logo-pulse"></div>}
                        </div>
                        <span className="sign-logo-text">{__elakayText("t004", "elakay")}</span>
                    </div>
                    
                    <AuthPageNav />
                    {enableDecorationAnimations && (
                        <div className="sign-header-decorations">
                            <BiWorld className="sign-decoration-icon sign-decoration-1" />
                            <div className="sign-decoration-line"></div>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="sign-main-content">
                {enableParticles && (
                    <div className="sign-particles">
                        {[...Array(particlesCount)].map((_, i) => (
                            <div key={i} className="sign-particle" style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 3}s`,
                                animationDuration: `${3 + Math.random() * 2}s`
                            }}></div>
                        ))}
                    </div>
                )}

                <div className="sign-form-container">
                    <div className={`sign-form-card ${!enableCardEntrance ? 'no-entrance' : ''}`}>
                        {enableCardGlow && <div className="sign-form-card-glow"></div>}
                        
                        {/* Activation Icon */}
                        <div className={`sign-activation-icon-wrapper ${!enableActivationIcon ? 'no-animation' : ''}`}>
                            <div className={`sign-activation-icon-circle ${!enableActivationIcon ? 'no-pulse' : ''} ${isCodeExpired ? 'expired' : ''}`}>
                                <FaShieldAlt className="sign-activation-icon" />
                            </div>
                        </div>

                        <form className="sign-form">
                            {/* Form Header */}
                            <div className="sign-form-header">
                                <h1 className={`sign-form-title ${!enableTitleGlow ? 'no-glow' : ''}`}>
                                    {isCodeExpired 
                                        ? (infoSource.laguangeElakay('signinPage', infoSource.languageAPI).titleExpired || "Code Expired")
                                        : (infoSource.laguangeElakay('signinPage', infoSource.languageAPI).titleActivation || "Account Activation")
                                    }
                                </h1>
                                {/* <p className="sign-form-subtitle">
                                    {isCodeExpired
                                        ? (infoSource.laguangeElakay('signinPage', infoSource.languageAPI).subtitleExpired || "Your verification code has expired")
                                        : (infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text0 || "Enter the code sent to your email")
                                    }
                                </p> */}
                            </div>

                         
                            {/* === COUNTDOWN TIMER === */}
                            {enableCountdownTimer && (
                                <div className="sign-active-countdown">
                                    {isLoadingAtivator ? (
                                        <div className="sign-countdown-wrapper">
                                            <div className="sign-countdown-loading">
                                                <div className="sign-dot"></div>
                                                <div className="sign-dot"></div>
                                                <div className="sign-dot"></div>
                                            </div>
                                            <span className="sign-loading-text">
                                                {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).loadingTimer || "Loading timer..."}
                                            </span>
                                        </div>
                                    ) : ativatorData && ativatorData.randomExpires ? (
                                        <CountdownTimer 
                                            randomExpires={ativatorData.randomExpires}
                                            experyDate={ativatorData.experyDate}
                                            createdAt={ativatorData._id ? getCreatedAtFromObjectId(ativatorData._id) : null}
                                            onExpired={handleCodeExpired}
                                            enableAnimation={enableCountdownAnimation}
                                            showProgressBar={enableCountdownProgressBar}
                                            showTimeText={enableCountdownTimeText}
                                            warningThreshold={countdownWarningThreshold}
                                            dangerThreshold={countdownDangerThreshold}
                                            infoSource={infoSource}
                                        />
                                    ) : null}
                                </div>
                            )}
   {/* Code Display */}
                        

                            {/* Description */}
                            {/* {!isCodeExpired && (
                                <p className="sign-activation-description">
                                    {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text2}
                                </p>
                            )} */}

                            {showLoading()}
                            {showError()}
                            {suscceSendCode()}

                            {/* Form Content - Afficher seulement si non expiré */}
                            {!isCodeExpired ? (
                                <>
                                    {/* Code Input -- shared 6-box OTP component (auto-advance,
                                        paste, autofill, auto-submit on the 6th digit) used
                                        identically here and in Signin.js's new-device modal. */}
                                    <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                        <label className="sign-input-label" id="activation-code-label">
                                            {infoSource.laguangeElakay('label', infoSource.languageAPI).securityCode || "Security Code"}
                                            <span style={{ color: 'red' }}> *</span>
                                        </label>
                                        <VerificationCodeInput
                                            value={numberDigi}
                                            onChange={(next) => setValues({ ...values, error: false, errorCode: null, Suc: false, numberDigi: next })}
                                            onComplete={handleAutomaticVerification}
                                            disabled={loading}
                                            hasError={Boolean(error)}
                                            ariaLabel={infoSource.laguangeElakay('label', infoSource.languageAPI).securityCode || "6-digit verification code"}
                                            autoFocus
                                        />
                                        <span className="sign-input-hint">{elakayAutoText("h_active_hint_code", "Enter the 6-digit code we sent to your email")}</span>
                                    </div>

                                    <input type="hidden" name="verication" placeholder="" />
                                    {/* Submit Button -- manual fallback; onComplete above already
                                        auto-submits as soon as the 6th digit is entered. */}
                                    <button
                                        type="button"
                                        className={`sign-btn-primary sign-btn-full ${enableButtonIcon ? 'sign-btn-animated' : ''} ${!enableButtonHover ? 'no-hover' : ''}`}
                                        onClick={() => clickSubmit()}
                                        disabled={loading}
                                    >
                                        <span>{infoSource.language(infoSource, 'Button', "submit") || elakayAutoText("h_8232d04b6a656b", "Activate Account")}</span>
                                        {enableButtonIcon && <MdArrowForward className="sign-btn-icon" />}
                                    </button>

                                    {/* Help Section */}
                                    {enableHelpSection && (
                                        <div className={`sign-activation-help ${!enableHelpSection ? 'no-animation' : ''}`}>
                                            <p className="sign-help-text">
                                                {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).text4 || 
                                                 "Didn't receive the code?"}
                                            </p>
                                            {enableResendButton && (
                                                <button 
                                                    type="button"
                                                    className={`sign-link-resend ${!enableResendButton ? 'no-animation' : ''}`}
                                                    onClick={() => resendCode()}
                                                    disabled={loading}
                                                >
                                                    {enableResendIcon && <MdRefresh className={`sign-resend-icon ${loading ? 'spinning' : ''}`} />}
                                                    {infoSource.laguangeElakay('Link', infoSource.languageAPI).newCode || "Resend Code"}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* Afficher les actions quand expiré */
                                <div className="sign-expired-actions">
                                    <div className="sign-expired-icon-wrapper">
                                        {/* <svg className="sign-expired-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <line x1="15" y1="9" x2="9" y2="15"/>
                                            <line x1="9" y1="9" x2="15" y2="15"/>
                                        </svg> */}
                                    </div>
                                    <p className="sign-expired-message">
                                        {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).expiredMessage || 
                                         "Your verification code has expired. Click below to receive a new code."}
                                    </p>
                                    <button 
                                        type="button"
                                        className={`sign-btn-primary sign-btn-full ${enableButtonIcon ? 'sign-btn-animated' : ''} ${!enableButtonHover ? 'no-hover' : ''}`}
                                        onClick={() => resendCode()}
                                        disabled={loading}
                                    >
                                        {enableButtonIcon && <MdRefresh className={`sign-btn-icon ${loading ? 'spinning' : ''}`} />}
                                        <span>{infoSource.laguangeElakay('Button', infoSource.languageAPI).resendCode || "Get New Code"}</span>
                                    </button>
                                    
                                    <div className="sign-divider">
                                        <span className="sign-divider-text">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).active.t002}</span>
                                    </div>
                                    
                                    <Link 
                                        className={`sign-btn-secondary sign-btn-full ${!enableButtonHover ? 'no-hover' : ''}`}
                                        to="/signup"
                                    >
                                        {infoSource.laguangeElakay('Button', infoSource.languageAPI).creatAccount || "Create New Account"}
                                    </Link>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </main>

            {/* Animated Footer */}
            <footer className={`sign-footer ${!enableFooterWaves ? 'no-waves' : ''}`}>
                {enableFooterWaves && (
                    <div className="sign-footer-waves">
                        <div className="sign-wave sign-wave-1"></div>
                        <div className="sign-wave sign-wave-2"></div>
                        <div className="sign-wave sign-wave-3"></div>
                    </div>
                )}
                
                <div className="sign-footer-content">
                    <p className="sign-footer-text">
                        © {new Date().getFullYear()}{__elakayText("t003", "elakay. All rights reserved.")}</p>
                    <div className="sign-footer-links">
                        <Link 
                            to="/privacy" 
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t005", "Privacy")}</Link>
                        <span className="sign-footer-separator">•</span>
                        <Link 
                            to="/terms" 
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t006", "Terms")}</Link>
                        <span className="sign-footer-separator">•</span>
                        <Link 
                            to="/help" 
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t007", "Help")}</Link>
                    </div>
                </div>
            </footer>
        </div>
    );

    // Opened while already signed in: leave for Home -- but not while
    // authenticate() is itself opening the destination after this activation
    // (redirecting here too used to add a second document load to /signin).
    const redirectUser = () => (isAuthenticated() && !redirectToReferrer ? <Navigate to="/" replace /> : null);

    return (
        <div className="sign-app-wrapper">
            {signUpForm()}
            {redirectUser()}
        </div>
    );
};

export default Active;
